import { useState } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { BareInput } from "components/DesignSystem/Input/Input";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { Cross } from "components/icons/Cross";
import { Pencil20 } from "components/icons/Pencil20";
import { SmallInfo } from "components/icons/SmallInfo";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Formik } from "formik";
import { EntityState } from "v2/store/onboardingEntityApi";
import ToolTip from "components/design/toolTip";
import { Home } from "components/icons/LeftNav/Home";
import { pinDelawareFirst } from "v2/utils/pinDelawareFirst";

const DATE_OF_INCORPORATION_REQUIRED = "Date of incorporation is required";
const STATE_OF_INCORPORATION_REQUIRED = "State of incorporation is required";

type StateOption = { value: string; label: string };

type EntityLocationSectionProps = {
  entityId: string;
  ein: string;
  dateOfIncorporation: string;
  states: EntityState[];
  showErrors?: boolean;
  onEinChange: (value: string) => void;
  onDoiChange: (value: string) => void;
  onSetHomeState: (id: string, name: string) => void;
  onAddState: (id: string, name: string) => void;
  onRemoveState: (id: string) => void;
};

const StateTag = ({
  name,
  onRemove,
  onEdit,
  isHome = false,
}: {
  name: string;
  onRemove?: () => void;
  onEdit?: () => void;
  isHome?: boolean;
}) => (
  <span className="t-flex t-items-center t-gap-1.5 t-rounded-full t-border t-border-solid t-border-purple-20 t-bg-purple-0 t-py-1 t-pl-3 t-pr-2 t-text-body-sm t-text-purple">
    {isHome && (
      <span className="t-flex">
        <Home size="12" />
      </span>
    )}
    {name}
    {onEdit && (
      <Button
        type="button"
        customType="link"
        onClick={onEdit}
        title="Change"
        size="small"
      >
        <Pencil20 className="t-size-3" />
      </Button>
    )}
    {onRemove && (
      <Button
        type="button"
        customType="danger_link"
        onClick={onRemove}
        title="Remove"
      >
        <Cross size="12" color="currentColor" />
      </Button>
    )}
  </span>
);

const AddStateButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="all:unset t-flex t-cursor-pointer t-items-center t-gap-1 t-rounded-full t-border t-border-dashed t-border-neutral-30 t-px-3 t-py-1 t-text-body-sm t-text-text-30 hover:t-border-purple hover:t-text-purple"
  >
    + Add
  </button>
);

const SectionLabel = ({
  label,
  required,
  tooltip,
}: {
  label: string;
  required?: boolean;
  tooltip?: string;
}) => (
  <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-60">
    {label}
    {required && <span className="t-text-red">*</span>}
    {tooltip && (
      <ToolTip text={tooltip}>
        <span className="t-text-text-30">
          <SmallInfo />
        </span>
      </ToolTip>
    )}
  </div>
);

export const EntityLocationSection = ({
  entityId,
  ein,
  dateOfIncorporation,
  states,
  showErrors = false,
  onEinChange,
  onDoiChange,
  onSetHomeState,
  onAddState,
  onRemoveState,
}: EntityLocationSectionProps) => {
  const [homeComboboxOpen, setHomeComboboxOpen] = useState(false);
  const [addStateOpen, setAddStateOpen] = useState(false);

  const { data: countries = [] } = useGetCountriesDataQuery();

  const hasEin = Boolean(ein);
  const doiFieldName = `dateOfIncorporation-${entityId}`;

  const usCountry = countries.find((c) => c.code_alpha_2 === "US");
  const homeState = states.find((s) => s.isHome);

  const showDateError = showErrors && !dateOfIncorporation;
  const showHomeStateError = showErrors && !homeState;
  const otherStates = states.filter((s) => !s.isHome);
  const otherStateIds = new Set(otherStates.map((s) => s.id));

  const allUsStates = usCountry?.states ?? [];

  const homeStateOptions = pinDelawareFirst(
    allUsStates
      .filter((s) => !otherStateIds.has(s.uuid))
      .map((s) => ({ value: s.uuid, label: s.name }))
  );

  const addStateOptions = pinDelawareFirst(
    allUsStates
      .filter((s) => s.uuid !== homeState?.id && !otherStateIds.has(s.uuid))
      .map((s) => ({ value: s.uuid, label: s.name }))
  );

  const handleHomeStateSelect = (opt: StateOption | null) => {
    if (!opt) return;
    onSetHomeState(opt.value, opt.label);
    setHomeComboboxOpen(false);
  };

  const handleOtherStateSelect = (opt: StateOption | null) => {
    if (!opt) return;
    onAddState(opt.value, opt.label);
    setAddStateOpen(false);
  };

  const handleChangeHomeState = () => setHomeComboboxOpen(true);

  const handleHomeComboboxChange = (opt: unknown) =>
    handleHomeStateSelect(opt as StateOption | null);

  const handleOtherComboboxChange = (opt: unknown) =>
    handleOtherStateSelect(opt as StateOption | null);

  return (
    <div className="t-flex t-flex-col t-gap-5">
      <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-4">
        {hasEin && (
          <BareInput
            label="EIN / Tax ID"
            placeholder="87-1234567"
            value={ein}
            onChange={(event) => onEinChange(event.target.value)}
          />
        )}
        <div className="t-flex t-flex-col t-gap-1">
          <Formik
            initialValues={{ [doiFieldName]: dateOfIncorporation }}
            enableReinitialize
            onSubmit={() => {}}
          >
            <DateField
              name={doiFieldName}
              label="Date of incorporation"
              required
              customType={showDateError ? "error" : undefined}
              portalId={`doi-${entityId}`}
              onDateChange={(date) =>
                onDoiChange(dayjs(date).format(YYYY_MM_DD))
              }
              placeholder="MMM DD, YYYY"
            />
          </Formik>
          {showDateError && (
            <HelperText variant="error">
              {DATE_OF_INCORPORATION_REQUIRED}
            </HelperText>
          )}
        </div>
      </div>

      <div>
        <div className="t-text-subtext t-text-text-60 t-mt-2">
          State presence
        </div>
        <div className="t-mb-4 t-text-body-sm t-text-neutral-40">
          States where you operate or have employees — needed for tax
          registrations
        </div>

        <div className="t-mb-4">
          <div className="t-flex t-gap-2">
            <SectionLabel
              label="State of incorporation"
              required
              tooltip="The state where your company is legally registered"
            />
            <div className="t-flex t-flex-wrap t-items-center t-gap-2">
              {homeState && !homeComboboxOpen && (
                <StateTag
                  isHome
                  name={homeState.name}
                  onEdit={handleChangeHomeState}
                />
              )}
              {!homeComboboxOpen && !homeState && (
                <AddStateButton onClick={() => setHomeComboboxOpen(true)} />
              )}
            </div>
          </div>
          {showHomeStateError && (
            <div className="t-mt-1">
              <HelperText variant="error">
                {STATE_OF_INCORPORATION_REQUIRED}
              </HelperText>
            </div>
          )}
          {homeComboboxOpen && (
            <div className="t-mt-2">
              <Combobox
                placeholder="Search and select a state..."
                options={homeStateOptions}
                value={null}
                onChange={handleHomeComboboxChange}
                onMenuClose={() => setHomeComboboxOpen(false)}
                menuPortalTarget={document.body}
                autoFocus
                menuIsOpen
              />
            </div>
          )}
        </div>

        <div>
          <div className="t-flex t-gap-2">
            <SectionLabel
              label="Other states"
              tooltip="Additional states where you have employees or business operations"
            />
            <div className="t-flex t-flex-wrap t-items-center t-gap-2">
              {otherStates.map((state) => (
                <StateTag
                  key={state.id}
                  name={state.name}
                  onRemove={() => onRemoveState(state.id)}
                />
              ))}
              {!addStateOpen && (
                <AddStateButton onClick={() => setAddStateOpen(true)} />
              )}
            </div>
          </div>
          {addStateOpen && (
            <div className="t-mt-2">
              <Combobox
                placeholder="Search and select a state..."
                options={addStateOptions}
                value={null}
                onChange={handleOtherComboboxChange}
                onMenuClose={() => setAddStateOpen(false)}
                menuPortalTarget={document.body}
                autoFocus
                menuIsOpen
                maxMenuHeight={180}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
